<template>
  <div class="flex flex-col p-2" style="width: 200px">
    <template v-if="!consented">
      <QIPDialog :image="selectedImage" @on-cancel="rateNegative(selectedImage)">
        <template #trigger>
          <!-- we need to disable this button through click interception and styles, otherwise disabling through the disabled
           attribute causes the click event to propagate up and still open the -->
          <MenuItem as="button" @click="dialogButtonOnClick" :class="{ 'disable-prism-button': reported }">
            <template #start>
              <InfoIcon v-if="!reported" />
              <TickIcon v-else />
            </template>
            <template #title>
              <span class="text-sm">{{ I18n.t("editor.magic_background.report_result") }}</span>
            </template>
          </MenuItem>
        </template>
      </QIPDialog>
    </template>
    <template v-else>
      <MenuItem as="button" @click="rateNegative(selectedImage)" :disabled="reported">
        <template #start>
          <InfoIcon v-if="!reported" />
          <TickIcon v-else />
        </template>
        <template #title>
          <span class="text-sm">{{ I18n.t("editor.magic_background.report_result") }}</span>
        </template>
      </MenuItem>
    </template>
    <hr class="border-surface-border my-2" />

    <!-- Delete confirmation dialog - only for processed HD images-->
    <Dialog v-if="requiresDeleteConfirmation" trigger-classes="focus:outline-none">
      <template #trigger>
        <MenuItem as="button" class="text-typo-critical">
          <template #start>
            <TrashIcon />
          </template>
          <template #title>
            <span class="text-sm">{{ I18n.t("editor.delete") }}</span>
          </template>
        </MenuItem>
      </template>
      <template v-slot:content="{ closeDialog }">
        <DeleteImageDialog
          :on-delete="
            () => {
              deleteImage();
              closeDialog();
            }
          "
          :on-cancel="closeDialog"
        />
      </template>
    </Dialog>

    <!-- Direct delete for images that don't require confirmation -->
    <MenuItem v-else as="button" class="text-typo-critical" @click="deleteImage">
      <template #start>
        <TrashIcon />
      </template>
      <template #title>
        <span class="text-sm">{{ I18n.t("editor.delete") }}</span>
      </template>
    </MenuItem>
  </div>
</template>

<script setup lang="ts">
import { InfoIcon, TickIcon, MenuItem, TrashIcon, Dialog } from "prism";
import { Image, ProcessingState } from "@/modules/internal_api/image";
import { inject, computed } from "vue";
import DeleteImageDialog from "./delete_image_dialog.vue";
import QIPDialog from "@/components/prism/qip_dialog.vue";
import { useImageReporter } from "@/composables/image_reporter";

interface Props {
  deleteImage: () => void;
  requiresDeleteConfirmation: boolean;
  selectedImage: Image;
}

const props = defineProps<Props>();
const I18n = inject("I18n");
const { consented, rateNegative } = useImageReporter();

const reported = computed(() => {
  if (props.selectedImage.previewResult.state === ProcessingState.Finished) {
    return props.selectedImage.previewResult?.rated;
  }
  return false;
});

const dialogButtonOnClick = (e: Event) => {
  if (reported.value) {
    e.stopPropagation();
  }
};
</script>

<style scoped>
.disable-prism-button {
  opacity: 0.25;
  cursor: default;
  &:hover::before {
    background-color: unset;
  }
}
</style>
